import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown, MessageCircle, Trash2, Plane, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiListQuotes, apiDeleteQuote } from "@/api";
import { useTenant } from "@/context/TenantContext";
import { formatINR } from "@/mock";
import { makeQuotePdf, shareQuote } from "@/components/FlightQuote";

const bookUrl = (s, it) => {
  const k = (f) => `${f.flightNo || ""}@${f.depTime || ""}`;
  const p = new URLSearchParams({
    tripType: s.retDate ? "round" : "one-way", from: s.from, fromCity: s.fromCity, to: s.to, toCity: s.toCity,
    dep: s.date, ret: s.retDate || "", ADT: s.adults || s.pax || 1, CHD: s.children || 0, INF: s.infants || 0,
    cabin: s.cabin || "Economy", pin: k(it.legs[0]),
  });
  if (it.legs[1]) p.set("pinr", k(it.legs[1]));
  return `/flight/search?${p.toString()}`;
};

const QuoteCard = ({ q, onDelete }) => {
  const brand = useTenant();
  const navigate = useNavigate();
  const s = q.search;
  const extra = { clientName: q.client_name, qno: q.qno };
  const pdf = async () => { const { doc, fileName } = await makeQuotePdf(brand, s, q.items, q.markup, extra); doc.save(fileName); };
  const share = () => shareQuote(brand, s, q.items, q.markup, extra).catch(() => {});
  return (
    <div data-testid={`saved-quote-${q.id}`} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{q.qno} · {new Date(q.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</p>
          <p className="mt-0.5 text-base font-extrabold text-slate-800">{s.from} {s.retDate ? "⇄" : "→"} {s.to} <span className="text-sm font-semibold text-slate-500">· {s.date}{s.retDate ? ` – ${s.retDate}` : ""} · {s.pax} pax</span></p>
          {q.client_name && <p className="text-xs font-semibold text-[#1B1F5B]">For {q.client_name}</p>}
        </div>
        <div className="ml-auto flex gap-2">
          <button data-testid={`saved-quote-pdf-${q.id}`} onClick={pdf} className="flex items-center gap-1 rounded-lg bg-[#1B1F5B] px-3 py-2 text-xs font-extrabold text-white"><FileDown className="h-3.5 w-3.5" />PDF</button>
          <button data-testid={`saved-quote-wa-${q.id}`} onClick={share} className="flex items-center gap-1 rounded-lg bg-[#25D366] px-3 py-2 text-xs font-extrabold text-white"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</button>
          <button data-testid={`saved-quote-del-${q.id}`} onClick={() => onDelete(q.id)} aria-label="Delete" className="rounded-lg border border-slate-200 px-2.5 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {q.items.map((it, i) => (
          <div key={it.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-3 py-2">
            <span className="text-[10px] font-extrabold text-slate-400">#{i + 1}</span>
            <div className="min-w-0 flex-1 space-y-0.5">
              {it.legs.map((f, li) => (
                <p key={li} className="truncate text-xs font-semibold text-slate-700"><Plane className="mr-1 inline h-3 w-3 text-slate-400" />{f.airline} {f.flightNo} · {f.from}-{f.to} · {f.depTime}-{f.arrTime}</p>
              ))}
            </div>
            <span className="text-sm font-extrabold text-[#1B1F5B]">{formatINR(q.totals?.[i] || 0)}</span>
            <button data-testid={`saved-quote-book-${q.id}-${i}`} onClick={() => navigate(bookUrl(s, it))}
              className="rounded-lg bg-[#F5B32B] px-3 py-1.5 text-xs font-extrabold text-[#0A0F2E] hover:bg-[#E89A10]">Book</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const SavedQuotes = () => {
  const [list, setList] = useState(null);
  useEffect(() => { apiListQuotes().then(setList).catch(() => setList([])); }, []);
  const del = async (id) => {
    try { await apiDeleteQuote(id); setList((l) => l.filter((q) => q.id !== id)); toast.success("Quotation deleted"); }
    catch { toast.error("Could not delete"); }
  };
  if (!list) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  return (
    <div data-testid="saved-quotes" className="space-y-3">
      <p className="text-xs text-slate-400">Fares are re-checked live when you press Book — the quoted flight appears at the top.</p>
      {list.length === 0 && <p data-testid="saved-quotes-empty" className="rounded-2xl bg-white p-8 text-center text-sm text-slate-400">No saved quotations yet. Search a flight, tick "Add to quote" and press Save quote.</p>}
      {list.map((q) => <QuoteCard key={q.id} q={q} onDelete={del} />)}
    </div>
  );
};
